/**
 * Operator Slack-lite chats (Admin / Production).
 * Collections: operatorChats/{id}, messages subcollection,
 * users/{uid}/operatorChatState, users/{uid}/operatorMessageReceipts (attention).
 */

import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    startAfter,
    Timestamp,
    where,
    writeBatch,
    increment,
    arrayUnion,
    arrayRemove,
} from 'firebase/firestore';
import { db } from './firebase';
import {
    displayNameFromUser,
    formatOperatorMessageTime,
    isOperatorMessageDue,
    listEligibleRecipients,
    markReceiptRead,
    MESSAGE_STATUS,
    OPERATOR_RECEIPTS_SUBCOLL,
    OPERATOR_SHOW_AFTER_PRESETS,
    resolveShowAfterTimestamp,
    subscribeUnreadInbox,
    userHasAdminOrProductionRole,
} from './operatorMessageService';
import { sanitizeOperatorMessageHtml } from '../utils/operatorMessageSanitize';
import {
    formatOperatorChatFileBytes,
    uploadOperatorChatFile,
    validateOperatorChatFile,
} from './operatorChatFileService';

export const OPERATOR_CHATS_COLL = 'operatorChats';
export const OPERATOR_CHAT_MESSAGES_SUBCOLL = 'messages';
export const OPERATOR_CHAT_STATE_SUBCOLL = 'operatorChatState';

export const CHAT_TYPE = {
    DM: 'dm',
    CHANNEL: 'channel',
};

export const CHAT_MEMBERSHIP = {
    EXPLICIT: 'explicit',
    ALL_ELIGIBLE: 'allEligible',
};

export const OPS_ALL_CHAT_ID = 'ops-all';
export const OPS_ALL_SLUG = 'ops-all';
export const OPS_ALL_NAME = 'All operators';

export const PINNED_MESSAGE_CAP = 50;
export const DEFAULT_MESSAGE_PAGE = 80;

/** Module-level focus hint when opening Messages from Got it. */
let pendingFocusChatId = null;

export function setPendingOperatorChatFocus(chatId) {
    pendingFocusChatId = chatId || null;
}

export function consumePendingOperatorChatFocus() {
    const id = pendingFocusChatId;
    pendingFocusChatId = null;
    return id;
}

function toMillis(value) {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (value instanceof Timestamp) return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Sorted DM key: dm:{uidA}_{uidB}
 * @param {string} uidA
 * @param {string} uidB
 */
export function buildDmMemberKey(uidA, uidB) {
    const a = String(uidA || '').trim();
    const b = String(uidB || '').trim();
    if (!a || !b) throw new Error('Both users are required for a DM.');
    const [x, y] = [a, b].sort();
    return `dm:${x}_${y}`;
}

function mapChatDoc(snap) {
    const data = snap.data() || {};
    return {
        id: snap.id,
        ...data,
        memberUids: Array.isArray(data.memberUids) ? data.memberUids : [],
        pinnedMessageIds: Array.isArray(data.pinnedMessageIds) ? data.pinnedMessageIds : [],
        createdAt: data.createdAt || null,
        lastMessageAt: data.lastMessageAt || null,
    };
}

function mapMessageDoc(snap) {
    const data = snap.data() || {};
    return {
        id: snap.id,
        ...data,
        attachments: Array.isArray(data.attachments) ? data.attachments : [],
        createdAt: data.createdAt || null,
        showAfter: data.showAfter || null,
        pinnedAt: data.pinnedAt || null,
    };
}

function mapStateDoc(snap) {
    const data = snap.data() || {};
    return {
        id: snap.id,
        chatId: snap.id,
        unreadCount: Number(data.unreadCount) || 0,
        lastReadAt: data.lastReadAt || null,
        updatedAt: data.updatedAt || null,
    };
}

function sortChatsByActivity(a, b) {
    const ta = toMillis(a.lastMessageAt) || toMillis(a.createdAt) || 0;
    const tb = toMillis(b.lastMessageAt) || toMillis(b.createdAt) || 0;
    return tb - ta;
}

/**
 * Ensure the system All operators channel exists.
 * @param {string} [createdByUid]
 */
export async function ensureOpsAllChannel(createdByUid = '') {
    const ref = doc(db, OPERATOR_CHATS_COLL, OPS_ALL_CHAT_ID);
    const snap = await getDoc(ref);
    if (snap.exists()) return mapChatDoc(snap);

    const payload = {
        type: CHAT_TYPE.CHANNEL,
        name: OPS_ALL_NAME,
        slug: OPS_ALL_SLUG,
        membership: CHAT_MEMBERSHIP.ALL_ELIGIBLE,
        memberUids: [],
        memberKey: null,
        createdBy: createdByUid || null,
        createdAt: serverTimestamp(),
        lastMessageAt: null,
        lastMessagePreview: '',
        lastMessageFromUid: null,
        pinnedMessageIds: [],
        isSelfNote: false,
    };
    await setDoc(ref, payload);
    return { id: OPS_ALL_CHAT_ID, ...payload };
}

/**
 * Open or create a DM (including self-note).
 * @param {{ fromUid: string, otherUid: string, fromUserData?: object, otherName?: string }} args
 */
export async function openOrCreateDm({ fromUid, otherUid, fromUserData, otherName }) {
    if (!fromUid || !otherUid) throw new Error('Both users are required.');
    if (!userHasAdminOrProductionRole(fromUserData || {})) {
        throw new Error('Only Admin and Production can use operator chats.');
    }

    const memberKey = buildDmMemberKey(fromUid, otherUid);
    const isSelfNote = fromUid === otherUid;
    const q = query(
        collection(db, OPERATOR_CHATS_COLL),
        where('memberKey', '==', memberKey),
        limit(1)
    );
    const existing = await getDocs(q);
    if (!existing.empty) {
        return mapChatDoc(existing.docs[0]);
    }

    const chatRef = doc(collection(db, OPERATOR_CHATS_COLL));
    const fromName = displayNameFromUser(fromUserData, fromUid);
    const peerName = isSelfNote
        ? 'Note to myself'
        : otherName || otherUid;
    const name = isSelfNote ? 'Note to myself' : peerName;

    const memberUids = isSelfNote ? [fromUid] : Array.from(new Set([fromUid, otherUid]));

    const payload = {
        type: CHAT_TYPE.DM,
        name,
        slug: null,
        membership: CHAT_MEMBERSHIP.EXPLICIT,
        memberUids,
        memberKey,
        createdBy: fromUid,
        createdAt: serverTimestamp(),
        lastMessageAt: null,
        lastMessagePreview: '',
        lastMessageFromUid: null,
        pinnedMessageIds: [],
        isSelfNote,
        dmPeerNames: isSelfNote
            ? { [fromUid]: fromName }
            : { [fromUid]: fromName, [otherUid]: peerName },
    };

    await setDoc(chatRef, payload);
    return { id: chatRef.id, ...payload };
}

/**
 * Create a custom channel with explicit members (must include creator).
 * @param {{
 *   createdByUid: string,
 *   fromUserData?: object,
 *   name: string,
 *   memberUids: string[],
 * }} args
 */
export async function createOperatorChannel({ createdByUid, fromUserData, name, memberUids }) {
    if (!createdByUid) throw new Error('Creator is required.');
    if (!userHasAdminOrProductionRole(fromUserData || {})) {
        throw new Error('Only Admin and Production can create channels.');
    }
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('Channel name is required.');

    const members = Array.from(
        new Set([createdByUid, ...(Array.isArray(memberUids) ? memberUids : [])].filter(Boolean))
    );
    if (members.length < 2) {
        throw new Error('Add at least one other operator to the channel.');
    }

    const chatRef = doc(collection(db, OPERATOR_CHATS_COLL));
    const payload = {
        type: CHAT_TYPE.CHANNEL,
        name: trimmed.slice(0, 80),
        slug: null,
        membership: CHAT_MEMBERSHIP.EXPLICIT,
        memberUids: members,
        memberKey: null,
        createdBy: createdByUid,
        createdAt: serverTimestamp(),
        lastMessageAt: null,
        lastMessagePreview: '',
        lastMessageFromUid: null,
        pinnedMessageIds: [],
        isSelfNote: false,
    };
    await setDoc(chatRef, payload);
    return { id: chatRef.id, ...payload };
}

/**
 * Whether the user may access this chat document.
 */
export function userCanAccessChat(chat, uid) {
    if (!chat || !uid) return false;
    if (chat.membership === CHAT_MEMBERSHIP.ALL_ELIGIBLE) return true;
    const members = Array.isArray(chat.memberUids) ? chat.memberUids : [];
    return members.includes(uid);
}

/**
 * Display title for sidebar / header.
 */
export function chatDisplayName(chat, currentUid) {
    if (!chat) return 'Chat';
    if (chat.type === CHAT_TYPE.CHANNEL) return chat.name || 'Channel';
    if (chat.isSelfNote) return 'Note to myself';
    const peers = chat.dmPeerNames || {};
    const otherUid = (chat.memberUids || []).find((id) => id !== currentUid);
    if (otherUid && peers[otherUid]) return peers[otherUid];
    if (otherUid) return otherUid;
    return chat.name || 'Direct message';
}

/**
 * Resolve recipient uids for attention / unread fan-out.
 * @param {object} chat
 * @param {string} fromUid
 * @returns {Promise<string[]>}
 */
async function resolveRecipientUids(chat, fromUid) {
    if (!chat) return [];
    if (chat.membership === CHAT_MEMBERSHIP.ALL_ELIGIBLE) {
        const eligible = await listEligibleRecipients(fromUid);
        return eligible.map((r) => r.uid).filter((uid) => uid !== fromUid);
    }
    const members = Array.isArray(chat.memberUids) ? chat.memberUids : [];
    if (chat.isSelfNote || (members.length === 1 && members[0] === fromUid)) {
        return [fromUid];
    }
    return members.filter((uid) => uid !== fromUid);
}

/**
 * Chunked Firestore batch commits (max ~400 ops per batch for safety).
 * @param {Array<(batch: import('firebase/firestore').WriteBatch) => void>} ops
 */
async function commitInChunks(ops, chunkSize = 400) {
    for (let i = 0; i < ops.length; i += chunkSize) {
        const slice = ops.slice(i, i + chunkSize);
        const batch = writeBatch(db);
        slice.forEach((apply) => apply(batch));
        await batch.commit();
    }
}

/**
 * Send a chat message (text and/or files).
 * @param {{
 *   chatId: string,
 *   fromUid: string,
 *   fromUserData?: object,
 *   bodyHtml?: string,
 *   files?: File[],
 *   driveItems?: object[],
 *   showAfterPresetId?: string,
 *   onUploadProgress?: (info: { fileIndex: number, pct: number, fileName: string }) => void,
 * }} args
 */
export async function sendChatMessage({
    chatId,
    fromUid,
    fromUserData,
    bodyHtml = '',
    files = [],
    driveItems = [],
    showAfterPresetId = 'now',
    onUploadProgress,
}) {
    if (!chatId || !fromUid) throw new Error('chatId and fromUid are required.');
    if (!userHasAdminOrProductionRole(fromUserData || {})) {
        throw new Error('Only Admin and Production can send operator chat messages.');
    }

    const chatRef = doc(db, OPERATOR_CHATS_COLL, chatId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) throw new Error('Chat not found.');
    const chat = mapChatDoc(chatSnap);
    if (!userCanAccessChat(chat, fromUid)) {
        throw new Error('You are not a member of this chat.');
    }

    const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
    for (const file of fileList) {
        const fileErr = validateOperatorChatFile(file);
        if (fileErr) throw new Error(fileErr);
    }

    const driveList = (Array.isArray(driveItems) ? driveItems : []).filter(
        (item) => item && item.id && item.type !== 'folder'
    );

    const { bodyHtml: cleanHtml, bodyText } = sanitizeOperatorMessageHtml(bodyHtml);
    if (!bodyText && fileList.length === 0 && driveList.length === 0) {
        throw new Error('Message cannot be empty — add text or a file.');
    }

    const messageRef = doc(collection(db, OPERATOR_CHATS_COLL, chatId, OPERATOR_CHAT_MESSAGES_SUBCOLL));
    const attachments = [];
    for (const item of driveList) {
        attachments.push({
            source: 'drive',
            itemId: item.id,
            fileName: item.name || item.originalFileName || 'file',
            sizeBytes: Number(item.sizeBytes) || 0,
            contentType: item.contentType || '',
            downloadUrl: item.downloadUrl || '',
            storagePath: item.storagePath || '',
        });
    }
    for (let i = 0; i < fileList.length; i += 1) {
        const file = fileList[i];
        const meta = await uploadOperatorChatFile({
            file,
            chatId,
            messageId: messageRef.id,
            onProgress: (pct) => {
                if (typeof onUploadProgress === 'function') {
                    onUploadProgress({
                        fileIndex: i,
                        pct,
                        fileName: file.name || 'file',
                    });
                }
            },
        });
        attachments.push(meta);
    }

    const fromName = displayNameFromUser(fromUserData, fromUid);
    const showAfter = resolveShowAfterTimestamp(showAfterPresetId);
    const now = serverTimestamp();
    const previewBits = [];
    if (bodyText) previewBits.push(bodyText.slice(0, 140));
    if (attachments.length) {
        const driveCount = attachments.filter((att) => att.source === 'drive').length;
        if (driveCount && driveCount === attachments.length) {
            previewBits.push(
                driveCount === 1
                    ? `Smoke Drive: ${attachments[0].fileName}`
                    : `${driveCount} Smoke Drive files`
            );
        } else {
            previewBits.push(
                attachments.length === 1
                    ? `File: ${attachments[0].fileName}`
                    : `${attachments.length} files`
            );
        }
    }
    const lastMessagePreview = previewBits.join(' · ').slice(0, 180);

    const messagePayload = {
        chatId,
        fromUid,
        fromName,
        bodyHtml: cleanHtml,
        bodyText,
        attachments,
        createdAt: now,
        showAfter: showAfter || null,
        showAfterPresetId: showAfterPresetId || 'now',
        pinned: false,
        pinnedAt: null,
        pinnedBy: null,
    };

    const recipients = await resolveRecipientUids(chat, fromUid);
    const ops = [];

    ops.push((batch) => {
        batch.set(messageRef, messagePayload);
        batch.update(chatRef, {
            lastMessageAt: now,
            lastMessagePreview,
            lastMessageFromUid: fromUid,
        });
    });

    const chatTitle = chatDisplayName(chat, fromUid);

    recipients.forEach((toUid) => {
        const isSelf = toUid === fromUid;
        const receiptRef = doc(db, 'users', toUid, OPERATOR_RECEIPTS_SUBCOLL, messageRef.id);
        const stateRef = doc(db, 'users', toUid, OPERATOR_CHAT_STATE_SUBCOLL, chatId);

        ops.push((batch) => {
            batch.set(receiptRef, {
                messageId: messageRef.id,
                chatId,
                fromUid,
                fromName,
                toUid,
                toName: isSelf ? 'Note to myself' : null,
                isSelfNote: isSelf,
                chatName: chatTitle,
                chatType: chat.type || CHAT_TYPE.DM,
                bodyHtml: cleanHtml,
                bodyText: bodyText || lastMessagePreview,
                attachments,
                createdAt: now,
                deliveredAt: now,
                showAfter: showAfter || null,
                showAfterPresetId: showAfterPresetId || 'now',
                status: MESSAGE_STATUS.DELIVERED,
                readAt: null,
            });
            batch.set(
                stateRef,
                {
                    chatId,
                    unreadCount: increment(1),
                    updatedAt: now,
                },
                { merge: true }
            );
        });
    });

    await commitInChunks(ops);

    return {
        messageId: messageRef.id,
        chatId,
        showAfter,
        attachments,
    };
}

/**
 * Pin a message in a chat.
 */
export async function pinChatMessage({ chatId, messageId, uid }) {
    if (!chatId || !messageId || !uid) return;
    const chatRef = doc(db, OPERATOR_CHATS_COLL, chatId);
    const messageRef = doc(db, OPERATOR_CHATS_COLL, chatId, OPERATOR_CHAT_MESSAGES_SUBCOLL, messageId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) throw new Error('Chat not found.');
    const chat = mapChatDoc(chatSnap);
    if (!userCanAccessChat(chat, uid)) throw new Error('Not a member of this chat.');

    const pinned = chat.pinnedMessageIds || [];
    if (pinned.includes(messageId)) return;
    if (pinned.length >= PINNED_MESSAGE_CAP) {
        throw new Error(`At most ${PINNED_MESSAGE_CAP} pinned messages per chat.`);
    }

    const batch = writeBatch(db);
    batch.update(messageRef, {
        pinned: true,
        pinnedAt: serverTimestamp(),
        pinnedBy: uid,
    });
    batch.update(chatRef, {
        pinnedMessageIds: arrayUnion(messageId),
    });
    await batch.commit();
}

/**
 * Unpin a message.
 */
export async function unpinChatMessage({ chatId, messageId, uid }) {
    if (!chatId || !messageId || !uid) return;
    const chatRef = doc(db, OPERATOR_CHATS_COLL, chatId);
    const messageRef = doc(db, OPERATOR_CHATS_COLL, chatId, OPERATOR_CHAT_MESSAGES_SUBCOLL, messageId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) throw new Error('Chat not found.');
    const chat = mapChatDoc(chatSnap);
    if (!userCanAccessChat(chat, uid)) throw new Error('Not a member of this chat.');

    const batch = writeBatch(db);
    batch.update(messageRef, {
        pinned: false,
        pinnedAt: null,
        pinnedBy: null,
    });
    batch.update(chatRef, {
        pinnedMessageIds: arrayRemove(messageId),
    });
    await batch.commit();
}

/**
 * Mark chat read for the current user (clears unread + related attention receipts).
 */
export async function markChatRead(uid, chatId) {
    if (!uid || !chatId) return;
    const stateRef = doc(db, 'users', uid, OPERATOR_CHAT_STATE_SUBCOLL, chatId);
    await setDoc(
        stateRef,
        {
            chatId,
            unreadCount: 0,
            lastReadAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );

    const receiptsQ = query(
        collection(db, 'users', uid, OPERATOR_RECEIPTS_SUBCOLL),
        where('chatId', '==', chatId),
        where('status', '==', MESSAGE_STATUS.DELIVERED)
    );
    try {
        const snap = await getDocs(receiptsQ);
        if (snap.empty) return;
        const ops = snap.docs.map(
            (receiptSnap) => (batch) => {
                batch.update(receiptSnap.ref, {
                    status: MESSAGE_STATUS.READ,
                    readAt: serverTimestamp(),
                });
            }
        );
        await commitInChunks(ops);
    } catch (err) {
        // Index may be missing until Console paste — unread state still cleared.
        console.warn('[operatorChat] markChatRead receipts:', err?.message || err);
    }
}

/**
 * Subscribe to chats visible to the user (member + allEligible channels).
 * @param {string} uid
 * @param {(chats: object[]) => void} onChange
 */
export function subscribeOperatorChats(uid, onChange) {
    if (!uid) {
        onChange([]);
        return () => {};
    }

    let memberChats = [];
    let allEligibleChats = [];

    const emit = () => {
        const byId = new Map();
        [...memberChats, ...allEligibleChats].forEach((c) => {
            if (c?.id) byId.set(c.id, c);
        });
        const list = Array.from(byId.values()).sort(sortChatsByActivity);
        onChange(list);
    };

    const qMember = query(
        collection(db, OPERATOR_CHATS_COLL),
        where('memberUids', 'array-contains', uid)
    );
    const qAll = query(
        collection(db, OPERATOR_CHATS_COLL),
        where('membership', '==', CHAT_MEMBERSHIP.ALL_ELIGIBLE)
    );

    const unsubMember = onSnapshot(
        qMember,
        (snap) => {
            memberChats = snap.docs.map(mapChatDoc);
            emit();
        },
        (err) => {
            console.error('[operatorChat] member chats subscribe failed:', err);
            memberChats = [];
            emit();
        }
    );

    const unsubAll = onSnapshot(
        qAll,
        (snap) => {
            allEligibleChats = snap.docs.map(mapChatDoc);
            emit();
        },
        (err) => {
            console.error('[operatorChat] allEligible chats subscribe failed:', err);
            allEligibleChats = [];
            emit();
        }
    );

    return () => {
        unsubMember();
        unsubAll();
    };
}

/**
 * Live message window (newest page), oldest→newest for render.
 * @param {string} chatId
 * @param {(payload: { messages: object[], loading: boolean }) => void} onChange
 * @param {{ pageSize?: number }} [opts]
 */
export function subscribeChatMessages(chatId, onChange, opts = {}) {
    if (!chatId) {
        onChange({ messages: [], loading: false });
        return () => {};
    }
    const pageSize = opts.pageSize || DEFAULT_MESSAGE_PAGE;
    const q = query(
        collection(db, OPERATOR_CHATS_COLL, chatId, OPERATOR_CHAT_MESSAGES_SUBCOLL),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
    );
    return onSnapshot(
        q,
        (snap) => {
            const messages = snap.docs.map(mapMessageDoc).reverse();
            onChange({ messages, loading: false });
        },
        (err) => {
            console.error('[operatorChat] messages subscribe failed:', err);
            onChange({ messages: [], loading: false });
        }
    );
}

/**
 * Load older messages before the oldest currently shown.
 * @returns {Promise<object[]>} older messages oldest→newest
 */
export async function loadOlderChatMessages(chatId, oldestCreatedAt, pageSize = DEFAULT_MESSAGE_PAGE) {
    if (!chatId || !oldestCreatedAt) return [];
    const q = query(
        collection(db, OPERATOR_CHATS_COLL, chatId, OPERATOR_CHAT_MESSAGES_SUBCOLL),
        orderBy('createdAt', 'desc'),
        startAfter(oldestCreatedAt),
        limit(pageSize)
    );
    const snap = await getDocs(q);
    return snap.docs.map(mapMessageDoc).reverse();
}

/**
 * Fetch pinned messages by ids (order follows pinnedMessageIds).
 */
export async function fetchPinnedMessages(chatId, pinnedMessageIds) {
    if (!chatId || !Array.isArray(pinnedMessageIds) || pinnedMessageIds.length === 0) {
        return [];
    }
    const results = [];
    for (const id of pinnedMessageIds) {
        const snap = await getDoc(
            doc(db, OPERATOR_CHATS_COLL, chatId, OPERATOR_CHAT_MESSAGES_SUBCOLL, id)
        );
        if (snap.exists()) results.push(mapMessageDoc(snap));
    }
    return results;
}

/**
 * Subscribe to per-user chat unread state map.
 * @param {string} uid
 * @param {(map: Record<string, object>) => void} onChange
 */
export function subscribeChatStates(uid, onChange) {
    if (!uid) {
        onChange({});
        return () => {};
    }
    const ref = collection(db, 'users', uid, OPERATOR_CHAT_STATE_SUBCOLL);
    return onSnapshot(
        ref,
        (snap) => {
            const map = {};
            snap.docs.forEach((d) => {
                map[d.id] = mapStateDoc(d);
            });
            onChange(map);
        },
        (err) => {
            console.error('[operatorChat] chat state subscribe failed:', err);
            onChange({});
        }
    );
}

export {
    displayNameFromUser,
    formatOperatorMessageTime,
    formatOperatorChatFileBytes,
    isOperatorMessageDue,
    listEligibleRecipients,
    markReceiptRead,
    MESSAGE_STATUS,
    OPERATOR_SHOW_AFTER_PRESETS,
    resolveShowAfterTimestamp,
    subscribeUnreadInbox,
    userHasAdminOrProductionRole,
};
